import { Injectable, signal, computed } from '@angular/core';
import { FeaturedAppInfoDTO } from '../DTOs/FeaturedAppInfoDTO';

@Injectable({
  providedIn: 'root'
})
export class AppStateService {

  // ╔══════════════════════════════════════════════════════════╗
  // ║ 1. Signal interna: lista global de aplicaciones destacadas║
  // ║    Esta es la fuente de verdad para toda la app          ║
  // ╚══════════════════════════════════════════════════════════╝
  private readonly _featuredApps = signal<FeaturedAppInfoDTO[]>([]);

  // ╔════════════════════════════════════════════════════════╗
  // ║ 2. Signal pública de solo lectura (para consumir global) ║
  // ║    Úsalo con computed/effect en cualquier componente    ║
  // ╚════════════════════════════════════════════════════════╝
  readonly featuredApps = this._featuredApps.asReadonly();

  // ╔═════════════════════════════════════════════════════════════╗
  // ║ 3. Setter: actualiza la lista global (ej. desde dashboard)  ║
  // ║    Se puede llamar cada 15 s o cuando se reciba nueva data  ║
  // ╚═════════════════════════════════════════════════════════════╝
  setFeaturedApps(apps: FeaturedAppInfoDTO[]): void {
    this._featuredApps.set(apps);
  }

  // ╔════════════════════════════════════════════════════════════╗
  // ║ 4. Snapshot sincrónico de las apps (sin reactividad)       ║
  // ║    Útil para lógica directa sin reactive bindings          ║
  // ╚════════════════════════════════════════════════════════════╝
  getAppsSnapshot(): FeaturedAppInfoDTO[] {
    return this._featuredApps();
  }

  // ╔═════════════════════════════════════════════════════════════╗
  // ║ 5. Buscar app por ID de medidor (útil en detalles, header) ║
  // ║    Devuelve null si no encuentra el medidor                ║
  // ╚═════════════════════════════════════════════════════════════╝
  getAppByMeterId(meterId: number): FeaturedAppInfoDTO | null {
    return this._featuredApps().find(app => app.meterId === meterId) ?? null;
  }

  // ╔═════════════════════════════════════════════════════════════╗
  // ║ 6. Computed: solo las apps con validación activa           ║
  // ║    Puedes usar este signal en componentes para mostrar     ║
  // ║    solo los medidores validados por CONAGUA o internos     ║
  // ╚═════════════════════════════════════════════════════════════╝
  readonly appsValidadas = computed(() =>
    this._featuredApps().filter(app => app.validado === 1)
  );

  // ╔═════════════════════════════════════════════════════════════╗
  // ║ 7. Limpieza: al cambiar de cliente o cerrar sesión          ║
  // ║    Evita que módulos como Producción/Balance, que leen esta ║
  // ║    lista global, muestren datos del cliente anterior.       ║
  // ╚═════════════════════════════════════════════════════════════╝
  clear(): void {
    this._featuredApps.set([]);
  }
}
